"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

type TocActiveValue = {
  activeId: string;
  setActiveId: Dispatch<SetStateAction<string>>;
};

const TocActiveContext = createContext<TocActiveValue>({
  activeId: "",
  setActiveId: () => {},
});

export const useTocActive = () => useContext(TocActiveContext);

// A heading becomes "current" once its top crosses this line, not the very top
// of the viewport — otherwise the highlight only flips when a heading is about
// to leave the screen.
const ACTIVE_OFFSET = 120;

type TocActiveProviderProps = {
  anchorIds?: string[];
  children: ReactNode;
};

/**
 * Holds the active-heading state for the Table of Contents.
 *
 * The list itself stays a Server Component: only this wrapper is client-side,
 * and the <li>/<a> markup arrives as `children`. The anchors read the active id
 * from context, so exactly one of them can be active at a time — each anchor
 * owning its own useState meant every clicked link stayed highlighted forever.
 */
const TocActiveProvider = ({ anchorIds = [], children }: TocActiveProviderProps) => {
  const [activeId, setActiveId] = useState("");

  // Array identity changes on every server render; key on the contents instead.
  const idsKey = anchorIds.join("|");

  useEffect(() => {
    const ids = idsKey ? idsKey.split("|") : [];
    if (!ids.length) return;

    let frame = 0;

    const sync = () => {
      frame = 0;

      const rendered = ids
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => Boolean(el));
      if (!rendered.length) return;

      // ids are in document order, so the last heading whose top has scrolled
      // past the offset is the one being read.
      let current = "";
      rendered.forEach((el) => {
        if (el.getBoundingClientRect().top - ACTIVE_OFFSET <= 0) current = el.id;
      });

      // Above the first heading: keep the first item lit rather than blank.
      if (!current) current = rendered[0].id;

      // The last headings can be too short to ever reach the offset line, so at
      // the bottom of the page force the final one active.
      const scrollBottom = window.scrollY + window.innerHeight;
      if (scrollBottom >= document.documentElement.scrollHeight - 2) {
        current = rendered[rendered.length - 1].id;
      }

      setActiveId((prev) => (prev === current ? prev : current));
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(sync);
    };

    sync();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [idsKey]);

  const value = useMemo(() => ({ activeId, setActiveId }), [activeId]);

  return <TocActiveContext.Provider value={value}>{children}</TocActiveContext.Provider>;
};

export default TocActiveProvider;
